const $$splitComponentImporter = () => import('/app/apps/web/src/routes/_app/artist/$id.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
const $$splitLoaderImporter = () => import('/app/apps/web/src/routes/_app/artist/$id.tsx?tsr-split=loader');
import { lazyFn } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { createTranslator } from "use-intl";
import { toAbsoluteUrl } from "@/lib/absolute-url";
import { resolveCatalogEntitySeo } from "@/lib/catalog-seo-policy";
import { ARTIST_DISCOGRAPHY_URL_CONFIG, pickNumberArray, pickReleaseSearchFilter } from "@/lib/faceted-search-url";
import { jsonLdScripts } from "@/lib/jsonLdScripts";
import { withSiteTitle } from "@/lib/page-title";
import { pickString, pickStringArray } from "@/lib/search-params";
import { buildSeoMetaTags } from "@/lib/seo-meta";
import { getRootLoaderData } from "@/types/intl";
interface ArtistSearch {
  q?: string;
  type?: string;
  sort?: string;
  format?: string;
  country?: string;
  label?: string[];
  company?: string[];
  year?: number[];
  filter?: ReleaseSearchFilter;
}
function buildArtistBreadcrumbItems(artist: {
  name: string;
  primaryIndex: {
    label: string;
    path: string;
  } | null;
}, id: string) {
  const currentArtist = {
    name: artist.name,
    url: `/artist/${id}`
  };
  if (!artist.primaryIndex) return [currentArtist];
  return [{
    name: artist.primaryIndex.label,
    url: artist.primaryIndex.path
  }, currentArtist];
}
export const Route = createFileRoute("/_app/artist/$id")({
  validateSearch: (raw: Record<string, unknown>): ArtistSearch => ({
    q: pickString(raw.q),
    type: pickString(raw.type),
    sort: pickString(raw.sort),
    format: pickString(raw.format),
    country: pickString(raw.country),
    label: pickStringArray(raw.label),
    company: pickStringArray(raw.company),
    year: pickNumberArray(raw.year),
    filter: pickReleaseSearchFilter(raw.filter, ARTIST_DISCOGRAPHY_URL_CONFIG)
  }),
  // Keep high-frequency text search out of loaderDeps so each keystroke does
  // not create a new route match. The loader still reads `location.search.q`
  // on route entry to SSR-prefetch direct `/artist/:id?q=...` loads.
  loaderDeps: ({
    search
  }) => ({
    type: search.type,
    sort: search.sort,
    format: search.format,
    country: search.country,
    label: search.label,
    company: search.company,
    year: search.year,
    filter: search.filter
  }),
  loader: lazyFn($$splitLoaderImporter, 'loader'),
  head: async ({
    match,
    loaderData,
    params
  }) => {
    const {
      locale,
      messages
    } = getRootLoaderData(match);
    const tMeta = createTranslator({
      locale,
      messages,
      namespace: "globals.metadata"
    });
    const tSeo = createTranslator({
      locale,
      messages,
      namespace: "globals.seo"
    });
    const tGenres = createTranslator({
      locale,
      messages,
      namespace: "shared.genres"
    });
    const tStyles = createTranslator({
      locale,
      messages,
      namespace: "shared.styles"
    });
    const artist = loaderData?.artist;
    if (!artist) {
      return {
        meta: [{
          title: withSiteTitle(tMeta("artist"))
        }]
      };
    }
    const {
      buildArtistSeoDescription,
      buildArtistSeoTitle,
      buildDefaultCommerceSeoFormat,
      formatAvailableTypeLabels,
      formatSeoCountryList,
      formatSeoNameList,
      formatSeoTaxonomyList
    } = await import("@/lib/seo");
    const {
      buildArtistJsonLd,
      buildCatalogPageJsonLd
    } = await import("@/utils/jsonld");
    const entitySeo = resolveCatalogEntitySeo({
      policy: artist.seo,
      currentEntityType: "artist",
      currentCatalogId: artist.id,
      locale
    });
    const typeLabels = formatAvailableTypeLabels(loaderData.discographyTypeCounts, tSeo, locale);
    const formats = buildDefaultCommerceSeoFormat({
      tSeo,
      locale
    });
    const title = buildArtistSeoTitle({
      name: artist.name,
      formats,
      tSeo
    });
    const country = formatSeoCountryList(artist.country ?? null, locale);
    const members = formatSeoNameList(artist.members?.map((member: {
      name: string;
    }) => member.name), locale);
    const tags = formatSeoTaxonomyList({
      genres: artist.genres,
      styles: artist.styles,
      translators: {
        genres: tGenres,
        styles: tStyles
      },
      locale
    });
    const description = buildArtistSeoDescription({
      name: artist.name,
      typeLabels,
      formats,
      country,
      members,
      tags,
      tSeo,
      locale
    });
    const imageUrl = toAbsoluteUrl(artist.imageUrl) ?? undefined;
    const url = entitySeo.canonicalUrl;
    const jsonLd = buildCatalogPageJsonLd({
      entity: artist,
      emitStructuredData: entitySeo.emitStructuredData,
      locale,
      buildEntityJsonLd: artist => buildArtistJsonLd(artist, description),
      buildBreadcrumbItems: artist => buildArtistBreadcrumbItems(artist, params.id)
    });
    return {
      meta: buildSeoMetaTags({
        title,
        description,
        canonicalUrl: url,
        imageUrl
      }, {
        robots: entitySeo.robots,
        openGraph: {
          type: "profile"
        },
        twitter: {}
      }),
      links: entitySeo.links,
      scripts: jsonLdScripts(jsonLd?.entity, jsonLd?.breadcrumb)
    };
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});